// Header 组件：负责显示页面头部区域，包含导航菜单、语言选择及钱包连接等功能

// 引入 React 的 useMemo 和 useState 钩子
import {
  useMemo,
  useState,
} from 'react';

// 引入 Ant Design 的 Dropdown 组件及相关类型
import {
  Dropdown,
  DropdownProps,
  MenuProps,
} from 'antd';
// 引入国际化工具 react-i18next 的钩子
import { useTranslation } from 'react-i18next';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

// 引入自定义的 Icon 组件和资源
import Icon from '@/components/Icon';
import useBodyBackground from '@/hooks/useBodyBackground';
import logo from '@/image/logo.png';
import menu from '@/image/menu.svg';
import { routeConfig } from '@/router';
// 引入语言状态管理工具
import useLanguage from '@/store/useLanguage';

// 引入 Connect 组件，用于钱包连接
import Connect from './components/Connect';

// 定义支持的语言选项
const itemsLanguage = [
  { key: 'zh', label: '简体中文' }, // 中文
  { key: 'en', label: 'English' }, // 英文
  { key: 'ko', label: '한국어' }, // 韩文
  { key: 'ja', label: '日本語' }, // 日文
];

// 定义 Header 组件
const Header = () => {
  const router = useNavigate(); // 路由跳转方法
  const { pathname } = useLocation(); // 当前路由路径
  // 控制主菜单的打开状态
  const [open, setOpen] = useState(false);

  // 获取当前语言及语言切换函数
  const { t, i18n: { language } } = useTranslation();
  const { setLanguage } = useLanguage(); // 状态管理中的语言设置方法
  const { i18n } = useTranslation();
  // 语言切换事件处理函数
  const onClick = ({ key }: { key: string }) => {
    i18n.changeLanguage(key); // 切换语言
    setLanguage(key); // 设置全局状态
    setOpen(false); // 关闭菜单
  };

  // 获取当前语言的显示文本
  const languageText = useMemo(() => {
    const selected = itemsLanguage.find(item => item.key === language);
    return selected ? selected.label : '';
  }, [language]);

  // 主菜单打开状态控制函数
  const handleOpenChange: DropdownProps['onOpenChange'] = (nextOpen, info) => {
    if (info.source === 'trigger' || nextOpen) {
      setOpen(nextOpen);
    }
  };

  // 设置菜单项，包含语言切换的嵌套下拉
  const items: MenuProps['items'] = [
    {
      key: '1',
      expandIcon: false,
      label: (
        <div className='flex items-center'>
          <div className='mr-14'>{t('header.language')}</div>
          <Dropdown
            onOpenChange={handleOpenChange}
            arrow
            menu={{ items: itemsLanguage, onClick }}
            trigger={['click']}
            placement="bottom"
          >
            <div className='pl-7.75'>{languageText}</div>
          </Dropdown>
        </div>
      ),
    },
  ];

  // 解析路由配置，提取所有设置了 header 的页面路径与标题
  const headerShow = useMemo(() => {
    const result: any[] = [];

    // 遍历路由配置，收集 header 为 true 的节点
    const traverse = (nodes: any[]) => {
      for (const node of nodes) {
        if (node.header && node.path) {
          result.push(node);
        }
        if (Array.isArray(node.children)) {
          traverse(node.children);
        }
      }
    };

    traverse(routeConfig);
    const headerRoutes = Array.from(new Set(result));
    const headerPath = headerRoutes.map(item => item.path);
    const current = headerRoutes.find((item) => pathname === item.path);

    return { current, headerPath, headerRoutes };
  }, [pathname]);

  // 根据 header 路由动态设置 body 背景色
  useBodyBackground(headerShow.headerPath);

  return (
    <div className={`${headerShow.current ? '' : 'hidden'}  relative bg-transparent h-11 flex justify-between items-center px-4 text-mainBlack md:h-16 md:px-12 md:flex`}>
      {/* 左侧标题（移动端） */}
      <div className='text-lg font-semibold md:hidden'>{t(`router.${headerShow.current?.headerTitle}`)}</div>
      <div className=' items-center font-semibold hidden md:flex'>
        <img src={logo} alt="" className='w-10 mr-2' />
        F-SWAP
      </div>
      {/* 中部导航菜单（桌面端） */}
      <div className='hidden items-center font-normal text-sm absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 md:flex'>
        {
          headerShow.headerRoutes.map((item, index) => {
            return <div key={index} onClick={() => router(item.path)} className={`cursor-pointer  text-lg font-medium mr-9 last:mr-0 ${pathname === item.path ? '' : 'text-ab'}`}>
              {t(`router.${item.headerTitle}`)}
            </div>
          })
        }
      </div>

      {/* 右侧菜单及钱包连接 */}
      <div className='flex items-center'>
        {/* 主菜单下拉（包含语言切换） */}
        <Dropdown
          onOpenChange={handleOpenChange}
          open={open}
          arrow
          menu={{ items }}
          trigger={['click']}
          placement="bottom"
        >
          <div>
            <Icon src={menu} className={"cursor-pointer w-6"} />
          </div>
        </Dropdown>
        {/* 钱包连接按钮 */}
        <Connect />
      </div>
    </div>
  );
};

export default Header;